import { isEmptySlateDocument } from "../../../core/constants";
import MediaGallery from "../../../media/website/MediaGallery";
import { GalleryDetails, Section } from "../../../resources/modelTypes";
import { hasProcessed } from "../../../website/components/ContentSection";
import SitePreviewContext from "../../../website/components/SitePreviewContext";
import { cloudinaryUrl } from "../../../website/utils";
import { ThemedRichText } from "./ThemedRichText";
import { css } from "@emotion/react";
import { SmallHeadline } from "@minted/fancyclothes";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo, useContext } from "react";

export type DisplayGallerySectionProps = {
  section: Section;
};
const sectionWidth = 1170;

const styles = {
  container: css`
    box-sizing: border-box;
    max-width: ${styleUtils.rem(sectionWidth)};
    overflow-wrap: break-word;
    text-align: center;
  `,
  description: css`
    // Images are full width on mobile, but text shouldn't be
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    margin-top: ${theme.spacing.x5};
    margin-bottom: ${styleUtils.rem(10)};
  `,
  oneColumnContentStyles: css`
    max-width: ${styleUtils.rem(600)};
    margin-left: auto;
    margin-right: auto;
  `,
  twoColumnContentStyles: css`
    max-width: ${styleUtils.rem((sectionWidth / 3) * 2)};
    margin-left: auto;
    margin-right: auto;
  `,
};

const DisplayGallerySection = ({ section }: DisplayGallerySectionProps) => {
  const sectionDetails = section.sectionDetails as GalleryDetails;
  const galleryMedia = section.galleryMedia ? section.galleryMedia : [];

  const mediaList = galleryMedia.map((media) => ({
    ...media,
    hasProcessed:
      media.mediaType === "video" ? hasProcessed(media.createdAt) : true,
    original:
      media.mediaType === "video"
        ? cloudinaryUrl(media.publicId, "jpg", "so_auto/h_2048,w_2048,c_fit")
        : media.original,
  }));

  let style;

  if (sectionDetails.galleryMode === "slideshow" || galleryMedia.length === 1) {
    style = styles.oneColumnContentStyles;
  } else if (galleryMedia.length === 2) {
    style = styles.twoColumnContentStyles;
  }

  const { isMobile } = useContext(SitePreviewContext);

  return (
    <div css={styles.container}>
      {Boolean(sectionDetails.title) && (
        <SmallHeadline>{sectionDetails.title}</SmallHeadline>
      )}
      <div css={style}>
        {!isEmptySlateDocument(sectionDetails.content) && (
          <div css={styles.description}>
            <ThemedRichText content={sectionDetails.content} />
          </div>
        )}
        {mediaList.length > 0 && (
          <MediaGallery
            isMobileView={isMobile}
            isPreview={sectionDetails.galleryMode === "slideshow"}
            media={mediaList}
          />
        )}
      </div>
    </div>
  );
};

const MemoizedDisplayGallery = memo(DisplayGallerySection);

export { MemoizedDisplayGallery as DisplayGallerySection };
